import React, { useState, useRef } from 'react';
import { Card, Button, Form } from "tabler-react";
import { useFetch } from '../../../lib/useFetch';
import postObject from '../../../lib/postObject';
import { useForm } from '../../../lib/useForm';
import Select from 'react-select';
import { toBase64 } from '../../../lib/helpers';

const SubCategoryCreateModal = ({ closeMainModal, reload }) => {
  const [form, handleChange] = useForm({ name: '', pricePerKg: '', minTravelerReward: '', weight: '' });
  const [category, setCategory] = useState(null);
  const { data: categories, loading: categoriesLoading } = useFetch(`categories`);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState('');
  const [iconPreview, setIconPreview] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const iconFile = useRef(null);
  const photoFile = useRef(null);

  const validateForm = () => {
    if (!form.name) {
      setError('Please enter subcategory name');
      return false;
    }
    if (!form.pricePerKg && form.pricePerKg !== 0) {
      setError('Please enter price per kg');
      return false;
    }
    if (!form.minTravelerReward && form.minTravelerReward !== 0) {
      setError('Please enter traveler reward');
      return false;
    }
    if (!form.weight && form.weight !== 0) {
      setError('Please enter weight');
      return false;
    }
    if (!category) {
      setError('Please select a category');
      return false;
    }
    if (iconFile.current.files.length === 0) {
      setError('Please select an icon');
      return false;
    }
    if (!iconFile.current.files[0].type.includes('image')) {
      setError('Please select a valid image file for icon');
      return false;
    }
    if (photoFile.current.files.length === 0) {
      setError('Please select a photo');
      return false;
    }
    if (!photoFile.current.files[0].type.includes('image')) {
      setError('Please select a valid image file for photo');
      return false;
    }
    return true;
  };

  const handleIconChange = async (e) => {
    const file = e.target.files[0];
    if (file && file.type.includes('image')) {
      const preview = await toBase64(file);
      setIconPreview(preview);
      setError('');
    } else if (file) {
      setError('Please select a valid image file for icon');
      setIconPreview(null);
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (file && file.type.includes('image')) {
      const preview = await toBase64(file);
      setPhotoPreview(preview);
      setError('');
    } else if (file) {
      setError('Please select a valid image file for photo');
      setPhotoPreview(null);
    }
  };

  const handleIconDropZoneClick = () => {
    iconFile.current.click();
  };

  const handlePhotoDropZoneClick = () => {
    photoFile.current.click();
  };

  const handleRemoveIcon = (e) => {
    e.stopPropagation();
    setIconPreview(null);
    iconFile.current.value = '';
    // Force reset the file input
    if (iconFile.current) {
      iconFile.current.type = '';
      iconFile.current.type = 'file';
    }
  };

  const handleRemovePhoto = (e) => {
    e.stopPropagation();
    setPhotoPreview(null);
    photoFile.current.value = '';
    // Force reset the file input
    if (photoFile.current) {
      photoFile.current.type = '';
      photoFile.current.type = 'file';
    }
  };

  const submitForm = async () => {
    setError('');
    if (!validateForm()) return;

    try {
      setSaveLoading(true);
      
      const subcategoryData = {
        ...form,
        categoryId: category.value,
        icon: await toBase64(iconFile.current.files[0]),
        image: await toBase64(photoFile.current.files[0])
      };
      
      await postObject('categories/sub-category', subcategoryData);
      
      if (typeof reload === 'function') {
        reload();
      }
      closeMainModal();
    } catch (err) {
      setError('Failed to create subcategory. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Card>
      <Card.Header>
        <Card.Title>Create Subcategory</Card.Title>
        <Card.Options>
          <Button
            size="sm"
            icon="x"
            onClick={closeMainModal}
          />
        </Card.Options>
      </Card.Header>
      <Card.Body>
        {error && <div className="alert alert-danger">{error}</div>}
        <Form onSubmit={(e) => { e.preventDefault(); submitForm(); }}>
          <Form.Input
            name='name'
            label='Name'
            placeholder='Enter subcategory name'
            value={form['name']}
            onChange={handleChange}
            required
          />

          <Form.Input
            name='pricePerKg'
            label='Price Per Kg'
            type="number"
            min="0"
            step="0.01"
            placeholder='Enter price per kilogram'
            value={form['pricePerKg']}
            onChange={handleChange}
            required
          />

          <Form.Input
            name='minTravelerReward'
            label='Traveler Reward'
            placeholder='Enter minimum traveler reward'
            type="number"
            min="0"
            step="0.01"
            value={form['minTravelerReward']}
            onChange={handleChange}
            required
          />

          <Form.Input
            name='weight'
            label='Weight'
            type="number"
            min="0"
            step="0.01"
            placeholder='Enter weight'
            value={form['weight']}
            onChange={handleChange}
            required
          />

          <Form.Label>Category *</Form.Label>
          <Select
            value={category}
            options={categories && categories.map(cat => {
              return ({ label: cat.name, value: cat['_id'] });
            })}
            isLoading={categoriesLoading}
            onChange={setCategory}
            placeholder="Select a category"
            isClearable
          />

          <Form.Group>
            <Form.Label>Icon *</Form.Label>
            <input 
              type='file' 
              ref={iconFile}
              accept="image/*"
              onChange={handleIconChange}
              style={{ display: 'none' }}
            />
            
            {!iconPreview ? (
              <div className="drop-zone" onClick={handleIconDropZoneClick}>
                <div className="drop-zone-icon">
                  <svg 
                    width="48" 
                    height="48" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="#6c757d" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21,15 16,10 5,21"/>
                  </svg>
                </div>
                <div className="drop-zone-text">Click here to upload subcategory icon</div>
                <div className="drop-zone-hint">JPG, PNG, GIF supported</div>
              </div>
            ) : (
              <div className="image-preview-container">
                <img 
                  src={iconPreview} 
                  alt="Icon Preview" 
                  className="image-preview"
                  onClick={handleIconDropZoneClick}
                />
                <button
                  type="button"
                  className="remove-image-btn"
                  onClick={handleRemoveIcon}
                >
                  ×
                </button>
              </div>
            )}
          </Form.Group>

          <Form.Group>
            <Form.Label>Photo *</Form.Label>
            <input 
              type='file' 
              ref={photoFile}
              accept="image/*"
              onChange={handlePhotoChange}
              style={{ display: 'none' }}
            />
            
            {!photoPreview ? (
              <div className="drop-zone" onClick={handlePhotoDropZoneClick}>
                <div className="drop-zone-icon">
                  <svg 
                    width="48" 
                    height="48" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="#6c757d" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21,15 16,10 5,21"/>
                  </svg>
                </div>
                <div className="drop-zone-text">Click here to upload subcategory photo</div>
                <div className="drop-zone-hint">JPG, PNG, GIF supported</div>
              </div>
            ) : (
              <div className="image-preview-container">
                <img 
                  src={photoPreview} 
                  alt="Photo Preview" 
                  className="image-preview"
                  onClick={handlePhotoDropZoneClick}
                />
                <button
                  type="button"
                  className="remove-image-btn"
                  onClick={handleRemovePhoto}
                >
                  ×
                </button>
              </div>
            )}
          </Form.Group>
        </Form>
      </Card.Body>
      <Card.Footer>
        <div className="modal-footer">
          <Button
            color="primary"
            size="sm"
            className={`ms-2 ${saveLoading ? 'btn-loading' : ''}`}
            onClick={submitForm}
            disabled={saveLoading}
          >
            Save
          </Button>
          <Button
            color="secondary"
            size="sm"
            className="ms-2"
            onClick={closeMainModal}
            disabled={saveLoading}
          >
            Cancel
          </Button>
        </div>
      </Card.Footer>
    </Card>
  );
};

export default SubCategoryCreateModal;